import { ExitToApp, PersonRemove } from '@mui/icons-material';
import { Button, Grid, Icon, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Group, ResolvedEvent, WaitingListType } from '@thro-types/events';

import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { TilesOrListView, TypedGridColumns } from 'app/components';
import { translations } from 'locales/i18n';

import { isEnrollable, isEventActive, isRegistrationPeriodOver } from '../../helper';
import { AddStudentModal } from './AddStudentModal';
import { EventCancelGroupModal } from './EventCancelGroupModal';
import { GroupCard } from './GroupCard';

interface Props {
  eventData: ResolvedEvent;
  markedGroup: number | null;
  canEdit: boolean;
  setMarkedGroup: Dispatch<SetStateAction<number | null>>;
  setGroupRef: (group: Group) => void;
  setEnrollingState: (state: 'ENROLLING' | 'UNENROLLING') => void;
  setEnrollingType: (type: WaitingListType) => void;
}

export const EventGroupOverview = ({
  eventData,
  markedGroup,
  canEdit,
  setMarkedGroup,
  setGroupRef,
  setEnrollingState,
  setEnrollingType
}: Props) => {
  const { t } = useTranslation();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [addStudentModalOpen, setAddStudentModalOpen] = useState(false);

  const [group, setGroup] = useState<Group | null>(null);

  const handleCancelGroup = selectedGroup => {
    setGroup(selectedGroup);
    setCancelModalOpen(true);
  };

  const handleEnrollment = useCallback(
    (group, enroll = true) => {
      setGroupRef(group);
      setEnrollingState(enroll ? 'ENROLLING' : 'UNENROLLING');
      setEnrollingType(WaitingListType.GROUP);
    },
    [setEnrollingState, setEnrollingType, setGroupRef]
  );

  //! ADD HERE THE CANCEL GROUP STUFF
  const rowActions = useCallback(
    params => {
      const action: JSX.Element[] = [];
      if (
        params.row.enrollments?.find(enrollment => enrollment.id === eventData.enrollmentId) ||
        params.row.waitingList?.enrollments?.find(
          enrollment => enrollment.id === eventData.enrollmentId
        ) ||
        eventData.enrollmentId === null
      ) {
        if (eventData.enrollmentId) {
          action.push(
            <GridActionsCellItem
              icon={<PersonRemove sx={{ color: 'red' }} />}
              label={t(translations.common.unregister())}
              onClick={() => {
                handleEnrollment(params.row, false);
              }}
              showInMenu
              disabled={
                !(eventData.isUnenrollAfterRegPeriodPossible
                  ? new Date() < new Date(eventData.startDate)
                  : new Date() < new Date(eventData.registrationPeriodEnd))
              }
            />
          );
        } else {
          action.push(
            <GridActionsCellItem
              icon={<ExitToApp sx={{ color: 'lightblue' }} />}
              label={
                isEnrollable(eventData)
                  ? t(translations.common.register())
                  : t(translations.pages.events.tooltips.enrollTooEarly())
              }
              onClick={() => {
                handleEnrollment(params.row, true);
              }}
              showInMenu
              disabled={!isEnrollable(eventData) || isRegistrationPeriodOver(eventData)}
            />
          );
        }
      }
      return action;
    },
    [eventData, t, handleEnrollment]
  );

  const columns = useMemo((): TypedGridColumns<Group> => {
    return [
      {
        field: 'id',
        flex: 1,
        headerName: t(translations.pages.events.create.eventName()),
        renderCell: params => {
          return (
            <>{`${t(translations.pages.events.create.groupCard.group())} ${
              (eventData.groups?.findIndex(g => g.id === params.row.id) || 0) + 1
            }`}</>
          );
        }
      },
      {
        field: 'info',
        flex: 3,
        headerName: t(translations.pages.events.create.groupCard.info()),
        valueGetter: (value, row) => row.info || t(translations.pages.events.notSpecified())
      },
      {
        field: 'place',
        flex: 2,
        headerName: t(translations.pages.events.create.groupCard.place()),
        valueGetter: (value, row) => row.place || t(translations.pages.events.notSpecified())
      },
      {
        field: 'minParticipants',
        flex: 1,
        headerName: t(translations.pages.events.eventDetailPage.minParticipants())
      },
      {
        field: 'maxParticipants',
        flex: 1,
        headerName: t(translations.pages.events.eventDetailPage.maxParticipants())
      },
      {
        flex: 1,
        field: 'enrollments',
        headerName: t(translations.pages.events.eventDetailPage.enrollments()),
        renderCell: params => <div>{params.row.enrollments?.length ?? 0}</div>
      },
      {
        field: 'waitingList',
        flex: 1,
        headerName: t(translations.pages.events.create.waitingList()),
        renderCell: params => <div>{params.row.waitingList?.enrollments?.length ?? 0}</div>
      },
      {
        flex: 1,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
        // renderCell: params => (
        //   <Tooltip
        //     title={
        //       isEnrollable(eventData) ? '' : t(translations.pages.events.tooltips.enrollTooEarly())
        //     }
        //   >
        //     {rowActions}
        //   </Tooltip>
        // )
      }
    ];
  }, [eventData.groups, rowActions, t]);

  const handleAddStudentModalClose = () => {
    setAddStudentModalOpen(false);
  };

  return (
    <Grid container className="group-overview">
      {group && (
        <EventCancelGroupModal
          open={cancelModalOpen}
          setOpen={setCancelModalOpen}
          group={group}
          eventData={eventData}
        />
      )}
      {canEdit && (
        <AddStudentModal
          open={addStudentModalOpen}
          closeHandler={handleAddStudentModalClose}
          eventData={eventData}
        />
      )}
      <Grid className="group-overview__information">
        <Grid container justifyContent={'space-between'}>
          <Typography component="h2">
            {t(translations.pages.events.eventDetailPage.groupOverview())}
          </Typography>
          {canEdit && !isMobile && (
            <Button
              startIcon={<Icon className="fas fa-plus" style={{ color: 'white' }} />}
              variant="contained"
              color="primary"
              style={{ color: 'white', marginLeft: '0.5em' }}
              onClick={() => setAddStudentModalOpen(true)}
            >
              {t(translations.pages.events.addParticipant())}
            </Button>
          )}
        </Grid>
        <Grid container style={{ width: '100%' }}>
          <TilesOrListView
            data={eventData.groups || []}
            height={'70vh'}
            renderTile={group => (
              <GroupCard
                group={group}
                event={eventData}
                markedGroup={markedGroup}
                canEdit={canEdit}
                setMarkedGroup={setMarkedGroup}
                handleEnrollment={handleEnrollment}
                handleCancelGroup={handleCancelGroup}
                key={group.id}
              />
            )}
            columns={columns}
            onRowClick={event => {
              setMarkedGroup(event.row.id);
            }}
            columnVisibilityModel={{
              actions: eventData.waitingType === WaitingListType.GROUP && isEventActive(eventData)
            }}
            getRowClassName={params => {
              return `highlightSelected--${markedGroup === params.row.id}`;
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
